import { PermissionAction, PermissionEffect, PrincipalType } from '@fleet/shared';
import type { DirectoryPrincipal, PermissionEntry } from '../../api/permissions';

export interface DraftEntry {
  action: PermissionAction;
  effect: PermissionEffect;
  principalType: PrincipalType;
  entraObjectId: string;
  displayName: string;
  /** Kam der Name in diesem Ladevorgang frisch aus dem Verzeichnis? */
  resolved: boolean;
  /** Nur für die Darstellung — seit dem Laden hinzugefügt. */
  isNew: boolean;
}

/**
 * Ein Eintrag ist über Aktion und Objekt-ID eindeutig, nicht über eine ID.
 *
 * Die API vergibt keine — sie liefert eine flache Liste, und genau dieses Tupel
 * trägt auch den Unique-Index in der Datenbank.
 */
export function keyOf(entry: {
  action: PermissionAction;
  principalType: PrincipalType;
  entraObjectId: string;
}): string {
  return `${entry.action}:${entry.principalType}:${entry.entraObjectId}`;
}

export type DraftEvent =
  | { type: 'reset'; entries: PermissionEntry[] }
  | { type: 'add'; action: PermissionAction; principal: DirectoryPrincipal }
  | { type: 'remove'; key: string }
  | { type: 'setEffect'; key: string; effect: PermissionEffect };

export function fromServer(entries: PermissionEntry[]): DraftEntry[] {
  return entries.map((entry) => ({ ...entry, isNew: false }));
}

/**
 * Der Entwurf hält **beide** Aktionen.
 *
 * Das ist keine Bequemlichkeit, sondern Pflicht: Der Speicher-Endpunkt ersetzt
 * die Berechtigungen einer Fahrzeuggruppe vollständig und ohne Filter auf die
 * Aktion. Wer nur die Buchungsrechte zurückschriebe, löschte damit alle
 * Freigaberechte still mit.
 */
export function draftReducer(state: DraftEntry[], event: DraftEvent): DraftEntry[] {
  switch (event.type) {
    case 'reset':
      return fromServer(event.entries);

    case 'add': {
      const candidate: DraftEntry = {
        action: event.action,
        effect: PermissionEffect.ALLOW,
        principalType: event.principal.principalType,
        entraObjectId: event.principal.entraObjectId,
        displayName: event.principal.displayName,
        resolved: true,
        isNew: true,
      };

      // Dubletten still übergehen: Wer denselben Eintrag zweimal auswählt, hat
      // nichts falsch gemacht — er hat nur etwas wiederholt.
      if (state.some((entry) => keyOf(entry) === keyOf(candidate))) return state;
      return [...state, candidate];
    }

    case 'remove':
      return state.filter((entry) => keyOf(entry) !== event.key);

    case 'setEffect':
      return state.map((entry) =>
        keyOf(entry) === event.key ? { ...entry, effect: event.effect } : entry,
      );
  }
}

/** Einträge einer Aktion, DENY zuoberst — in der Reihenfolge ihrer Auswertung. */
export function entriesOf(state: DraftEntry[], action: PermissionAction): DraftEntry[] {
  return state
    .filter((entry) => entry.action === action)
    .sort((a, b) => {
      if (a.effect !== b.effect) return a.effect === PermissionEffect.DENY ? -1 : 1;
      if (a.principalType !== b.principalType) {
        return a.principalType === PrincipalType.USER ? -1 : 1;
      }
      return a.displayName.localeCompare(b.displayName, 'de');
    });
}

/** Weicht der Entwurf vom geladenen Stand ab? */
export function isDirty(draft: DraftEntry[], server: PermissionEntry[]): boolean {
  const fingerprint = (entries: { action: PermissionAction; effect: PermissionEffect; principalType: PrincipalType; entraObjectId: string }[]) =>
    entries
      .map((entry) => `${keyOf(entry)}:${entry.effect}`)
      .sort()
      .join('|');

  return fingerprint(draft) !== fingerprint(server);
}

/** Nutzlast für das Speichern — ohne die reinen Anzeigefelder. */
export function toPayload(draft: DraftEntry[]) {
  return draft.map(({ action, effect, principalType, entraObjectId, displayName }) => ({
    action,
    effect,
    principalType,
    entraObjectId,
    displayName,
  }));
}
